import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootStoreState } from "@store/types";

import type { Product } from "@jti/magento";
import { getCurrentMagentoClient, hasBundleDiscontinuedProduct } from "@jti/magento";

import { setDiscontinuedProductsErrors, setFreeGiftsErrors } from "../../cartSlice";
import type { AvailableFreeGift, DiscontinuedProductError, FreeGiftError } from "../../cartSlice.types";
import { fetchCartPrices, fetchTrialCartPrices } from "../fetchCartPrices";

type CartItemsResult = { items: Product[]; availableFreeGifts: AvailableFreeGift[] };

export type FetchCartItemsArg = string | { cartId: string; silent?: boolean };

const getItemIds = (items: Product[]) => items.map((item) => item.id || "").filter((id) => !!id);

const dispatchPrices = (dispatch: (action: unknown) => void, cartId: string, isFreeTrial: boolean) => {
  dispatch(isFreeTrial ? fetchTrialCartPrices(cartId) : fetchCartPrices(cartId));
};

const removeInvalidItems = async (
  cartId: string,
  isFreeTrial: boolean,
  invalidItems: Product[]
): Promise<CartItemsResult> => {
  const client = getCurrentMagentoClient(isFreeTrial);
  await client.removeItemsFromCart(cartId, getItemIds(invalidItems));
  return client.getCartItems(cartId);
};

const buildFreeGiftErrors = (freeGiftsWithErrors: Product[]): FreeGiftError[] =>
  freeGiftsWithErrors
    .flatMap((item) =>
      (item.errors || []).map((error) => ({
        errorCode: error.code,
        parentId: item.parentGiftId,
        parentSku: item.parentGiftSku,
      }))
    )
    .filter((error) => !!error);

const fetchItemsDeclaration = (isFreeTrial: boolean) =>
  createAsyncThunk(
    isFreeTrial ? "freeTrialCart/fetchCartItems" : "cart/fetchCartItems",
    async (arg: FetchCartItemsArg, { dispatch }): Promise<CartItemsResult> => {
      const cartId = typeof arg === "string" ? arg : arg.cartId;
      const items = await getCurrentMagentoClient(isFreeTrial).getCartItems(cartId);

      dispatchPrices(dispatch, cartId, isFreeTrial);

      return items;
    }
  );

const fetchCartItemsWithValidations = (isFreeTrial: boolean) =>
  createAsyncThunk<CartItemsResult, string, { state: RootStoreState }>(
    isFreeTrial ? "freeTrialCart/fetchCartItemsWithValidation" : "cart/fetchCartItemsWithValidation",
    async (cartId: string, { dispatch }): Promise<CartItemsResult> => {
      let items = await getCurrentMagentoClient(isFreeTrial).getCartItems(cartId);

      const freeGiftsWithErrors = items.items.filter((item) => item.isGift && !!item.errors);
      const discontinuedProducts = items.items.filter(
        (item) => item.isDiscontinued || hasBundleDiscontinuedProduct(item)
      );

      const allInvalidItems = [...freeGiftsWithErrors, ...discontinuedProducts];

      if (allInvalidItems.length > 0) {
        items = await removeInvalidItems(cartId, isFreeTrial, allInvalidItems);

        if (freeGiftsWithErrors.length > 0) {
          const freeGiftErrors: FreeGiftError[] = buildFreeGiftErrors(freeGiftsWithErrors);

          dispatch(setFreeGiftsErrors(freeGiftErrors));
        }

        if (discontinuedProducts.length > 0) {
          const discontinuedErrors: DiscontinuedProductError[] = discontinuedProducts.map((item) => ({
            sku: item.sku,
            name: item.name,
          }));

          dispatch(setDiscontinuedProductsErrors(discontinuedErrors));
        }
      }

      dispatchPrices(dispatch, cartId, isFreeTrial);

      return items;
    },
    {
      condition: (cartId, { getState }) => isFreeTrial || getState().cart.pendingItemsCartId !== cartId,
    }
  );

export type RemoveInvalidFreeGiftsArg = { cartId: string; items: Product[] };

const removeInvalidFreeGiftsDeclaration = (isFreeTrial: boolean) =>
  createAsyncThunk(
    isFreeTrial ? "freeTrialCart/removeInvalidFreeGifts" : "cart/removeInvalidFreeGifts",
    async ({ cartId, items }: RemoveInvalidFreeGiftsArg, { dispatch }): Promise<void> => {
      const freeGiftsWithErrors = items.filter((item) => item.isGift && !!item.errors);

      if (freeGiftsWithErrors.length === 0) {
        return;
      }

      await getCurrentMagentoClient(isFreeTrial).removeItemsFromCart(cartId, getItemIds(freeGiftsWithErrors));

      dispatch(setFreeGiftsErrors(buildFreeGiftErrors(freeGiftsWithErrors)));
    }
  );

export const fetchCartItems = fetchItemsDeclaration(false);
export const fetchTrialCartItems = fetchItemsDeclaration(true);
export const fetchCleanCartItems = fetchCartItemsWithValidations(false);
export const fetchTrialCleanCartItems = fetchCartItemsWithValidations(true);
export const removeInvalidFreeGifts = removeInvalidFreeGiftsDeclaration(false);
export const removeTrialInvalidFreeGifts = removeInvalidFreeGiftsDeclaration(true);
